import React, { useMemo, useState } from 'react';
import { RotateCcw, Search } from 'lucide-react';

import { formatDate, formatMontant, formatNombre, symboleDevise } from '../../lib/formats';
import { pastillesLigne } from '../../lib/lotSettlements';
import { nombreSaisi, texteChamp } from '../../lib/transportStatements';
import { cn, formatVolume } from '../../lib/utils';
import { usePagination } from '../../hooks/usePagination';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { EmptyState } from '../ui/empty-state';
import { Input } from '../ui/input';
import { BarrePagination } from '../ui/pagination-bar';
import { SearchableMultiSelect } from '../ui/searchable-multi-select';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../ui/table';
import { CelluleMontant } from './paiementsUi';

/** Les camions du menu à choix multiple : tracteur, puis citerne · N° bon · quantité. */
export const optionsCamions = (lignes) =>
  (lignes || []).map((ligne) => ({
    value: ligne.lot_truck_id,
    label: ligne.tractor_plate || '—',
    description: [
      ligne.tank_plate,
      ligne.loading_voucher_number ? `N° bon ${ligne.loading_voucher_number}` : null,
      ligne.quantity !== null && ligne.quantity !== undefined ? formatVolume(ligne.quantity) : null,
    ]
      .filter(Boolean)
      .join(' · '),
    keywords: [ligne.tank_plate, ligne.loading_voucher_number].filter(Boolean),
  }));

/** Le prix au litre : entier quand il l'est, sinon deux décimales. */
const prixLitre = (valeur) =>
  valeur === null || valeur === undefined ? '—' : formatNombre(valeur, Number.isInteger(Number(valeur)) ? 0 : 2, '—');

/** Les pastilles d'une ligne : ce qui reste à revoir, sans phrase. */
const Pastilles = ({ ligne }) => {
  const pastilles = pastillesLigne(ligne);
  if (pastilles.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {pastilles.map((pastille) => (
        <Badge key={pastille.texte} variant={pastille.ton} title={pastille.titre}>
          {pastille.texte}
        </Badge>
      ))}
    </div>
  );
};

/** Le texte d'une valeur de ligne : prix au litre (sans unité, comme la colonne) ou montant. */
const texteValeur = (valeur, devise, prix) => {
  if (valeur === null || valeur === undefined) return '—';
  return prix ? prixLitre(valeur) : formatMontant(valeur, devise);
};

/** La valeur calculée, avec son unité, pour l'infobulle d'une valeur saisie. */
const texteDefaut = (defaut, devise, prix) =>
  prix ? `${prixLitre(defaut)} ${symboleDevise(devise)}/L` : texteValeur(defaut, devise, false);

/** Marque discrète d'une valeur saisie à la main ; la valeur calculée est dans l'infobulle. */
export const MarqueModifie = ({ defaut, devise = 'XOF', prix = false, testid }) => (
  <Badge
    variant="info"
    className="px-1.5 py-0 text-[10px] leading-4"
    title={`Par défaut : ${texteDefaut(defaut, devise, prix)}`}
    data-testid={testid}
  >
    Modifié
  </Badge>
);

/**
 * Une valeur de ligne qu'on peut remplacer : affichée, elle devient un champ au
 * clic. La saisie part à chaque frappe (`onSaisie(texte)`) ; vider le champ,
 * retaper la valeur calculée ou ⟲ reviennent au calcul (`onSaisie('')`).
 */
const ValeurModifiable = ({
  valeur,
  defaut,
  texte = '',
  saisi = false,
  devise,
  prix = false,
  modifiable = false,
  libelle,
  testid,
  onSaisie,
}) => {
  const [edition, setEdition] = useState(false);
  const [brouillon, setBrouillon] = useState('');
  const affiche = texteValeur(valeur, devise, prix);

  const ouvrir = () => {
    setBrouillon(saisi ? texte : texteChamp(defaut));
    setEdition(true);
  };
  const changer = (suivant) => {
    setBrouillon(suivant);
    const nombre = nombreSaisi(suivant);
    const commeLeCalcul = nombre !== null && defaut !== null && defaut !== undefined && nombre === Number(defaut);
    onSaisie(commeLeCalcul ? '' : suivant);
  };

  if (edition && modifiable) {
    return (
      <Input
        type="number"
        inputMode="decimal"
        min="0"
        step="any"
        autoFocus
        value={brouillon}
        onChange={(evenement) => changer(evenement.target.value)}
        onBlur={() => setEdition(false)}
        onKeyDown={(evenement) => {
          if (evenement.key === 'Enter' || evenement.key === 'Escape') {
            evenement.preventDefault();
            evenement.currentTarget.blur();
          }
        }}
        aria-label={libelle}
        className="ml-auto h-8 w-32 text-right tabular-nums"
        data-testid={`${testid}-saisie`}
      />
    );
  }
  return (
    <div className="flex items-center justify-end gap-1">
      {saisi ? <MarqueModifie defaut={defaut} devise={devise} prix={prix} testid={`${testid}-modifie`} /> : null}
      {modifiable ? (
        <button
          type="button"
          onClick={ouvrir}
          aria-label={`${libelle} : ${affiche}`}
          className="-mx-1 rounded px-1 underline decoration-muted-foreground/50 decoration-dotted underline-offset-4 hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          data-testid={testid}
        >
          {affiche}
        </button>
      ) : (
        <span data-testid={testid}>{affiche}</span>
      )}
      {saisi && modifiable ? (
        <button
          type="button"
          onClick={() => onSaisie('')}
          aria-label={`${libelle} : revenir au calcul`}
          title={`Revenir à ${texteDefaut(defaut, devise, prix)}`}
          className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          data-testid={`${testid}-retablir`}
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
};

/** Une valeur figée (décompte validé ou annulé), marquée si elle a été saisie à la main. */
const ValeurLue = ({ valeur, defaut, saisi, devise, prix = false, testid }) => (
  <div className="flex items-center justify-end gap-1">
    {saisi ? <MarqueModifie defaut={defaut} devise={devise} prix={prix} testid={`${testid}-modifie`} /> : null}
    <span>{texteValeur(valeur, devise, prix)}</span>
  </div>
);

const estSaisi = (valeur) => valeur !== null && valeur !== undefined;

/**
 * Les camions dépotés qu'un décompte peut porter : case à cocher, dépoté à
 * saisir quand le manquant est inconnu, montants en aperçu tant que le
 * décompte n'est pas enregistré. Sur un camion choisi, le prix, le montant et
 * la retenue se remplacent à la main (`onManuel(camion, 'prix' | 'montant' |
 * 'retenue', texte)`, texte vide pour revenir au calcul).
 */
export const CamionsDecompte = ({
  lignes,
  saisie,
  totaux,
  devise = 'XOF',
  disabled = false,
  onBasculer,
  onBasculerTous,
  onLivre,
  onManuel,
}) => {
  const [recherche, setRecherche] = useState('');
  const [selectionSeule, setSelectionSeule] = useState(false);

  const filtrees = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    return lignes.filter((ligne) => {
      if (selectionSeule && !saisie.selectionnes.has(ligne.lot_truck_id)) return false;
      if (!terme) return true;
      return [ligne.tractor_plate, ligne.tank_plate, ligne.loading_voucher_number].some((texte) =>
        String(texte || '').toLowerCase().includes(terme),
      );
    });
  }, [lignes, recherche, selectionSeule, saisie.selectionnes]);

  const pagination = usePagination(filtrees, {
    cle: 'camions',
    reinitialiser: `${recherche}|${selectionSeule}`,
  });

  const options = useMemo(() => optionsCamions(lignes), [lignes]);
  const valeursChoisies = useMemo(() => [...saisie.selectionnes], [saisie.selectionnes]);
  // Le menu et le tableau cochent les mêmes lignes, par les mêmes gestes.
  const changerDepuisMenu = (_suivants, { values, selected }) => {
    if (values.length === 1) onBasculer(values[0]);
    else onBasculerTous(values, selected);
  };

  const choisis = saisie.selectionnes.size;
  const tousChoisis = filtrees.length > 0 && filtrees.every((ligne) => saisie.selectionnes.has(ligne.lot_truck_id));
  const certainsChoisis = filtrees.some((ligne) => saisie.selectionnes.has(ligne.lot_truck_id));
  const etatCaseTous = tousChoisis ? true : certainsChoisis ? 'indeterminate' : false;

  return (
    <section className="space-y-2" aria-label="Camions du décompte" data-testid="decompte-camions">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-bold text-foreground">Camions dépotés</h2>
        <Badge variant={choisis > 0 ? 'info' : 'muted'} data-testid="decompte-compteur">
          {choisis} sur {lignes.length}
        </Badge>
        <div className="relative w-56">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            value={recherche}
            onChange={(evenement) => setRecherche(evenement.target.value)}
            placeholder="Tracteur, citerne ou bon"
            aria-label="Rechercher un camion"
            className="h-8 pl-8 text-xs"
            data-testid="decompte-recherche"
          />
        </div>
        {lignes.length > 0 ? (
          <SearchableMultiSelect
            value={valeursChoisies}
            onValueChange={changerDepuisMenu}
            options={options}
            placeholder="Choisir des camions"
            searchPlaceholder="Tracteur, citerne ou bon"
            emptyMessage="Aucun camion ne correspond"
            unite="camion"
            disabled={disabled}
            aria-label="Choisir des camions"
            data-testid="decompte-menu-camions"
          />
        ) : null}
        <Button
          type="button"
          variant={selectionSeule ? 'default' : 'outline'}
          size="sm"
          aria-pressed={selectionSeule}
          onClick={() => setSelectionSeule((actif) => !actif)}
          className="h-8 text-xs"
          data-testid="decompte-selection-seule"
        >
          Choisis
        </Button>
      </div>

      {lignes.length === 0 ? (
        <EmptyState illustration="donnee" title="Aucun camion dépoté à décompter" />
      ) : filtrees.length === 0 ? (
        <EmptyState variant="filtered" title="Aucun camion ne correspond" />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-border/70">
            <Table stickyFirstColumn={false} data-testid="decompte-table-camions">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <Checkbox
                      checked={etatCaseTous}
                      disabled={disabled}
                      onCheckedChange={(valeur) =>
                        onBasculerTous(
                          filtrees.map((ligne) => ligne.lot_truck_id),
                          valeur === true,
                        )
                      }
                      aria-label="Choisir tous les camions"
                      data-testid="decompte-choisir-tous"
                    />
                  </TableHead>
                  <TableHead>Chargement</TableHead>
                  <TableHead>N° bon</TableHead>
                  <TableHead>Tracteur</TableHead>
                  <TableHead>Citerne</TableHead>
                  <TableHead className="text-right">Quantité</TableHead>
                  <TableHead className="text-right">Prix</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead className="text-right">Manquant</TableHead>
                  <TableHead className="text-right">Retenue</TableHead>
                  <TableHead className="text-right">Solde</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagination.lignes.map((ligne) => {
                  const choisi = saisie.selectionnes.has(ligne.lot_truck_id);
                  const discret = !choisi;
                  const id = ligne.lot_truck_id;
                  const plaque = ligne.tractor_plate || '';
                  const manuel = saisie.manuels?.[id] || {};
                  const saisis = ligne.saisis || {};
                  // Seul un camion choisi reçoit une valeur saisie.
                  const modifiable = choisi && !disabled && Boolean(onManuel);
                  const valeur = (cle, champ, libelle, options = {}) => (
                    <ValeurModifiable
                      valeur={ligne[cle]}
                      defaut={ligne[`${cle}Defaut`]}
                      texte={manuel[cle] ?? ''}
                      saisi={Boolean(saisis[cle])}
                      devise={devise}
                      modifiable={modifiable}
                      libelle={`${libelle} du camion ${plaque}`.trim()}
                      testid={`decompte-${champ}-${id}`}
                      onSaisie={(texte) => onManuel(id, cle, texte)}
                      {...options}
                    />
                  );
                  return (
                    <TableRow
                      key={ligne.lot_truck_id}
                      data-state={choisi ? 'selected' : undefined}
                      data-testid={`decompte-ligne-${ligne.lot_truck_id}`}
                    >
                      <TableCell>
                        <Checkbox
                          checked={choisi}
                          disabled={disabled}
                          onCheckedChange={() => onBasculer(ligne.lot_truck_id)}
                          aria-label={`Choisir le camion ${ligne.tractor_plate || ''}`.trim()}
                        />
                      </TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">{formatDate(ligne.loading_date)}</TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">{ligne.loading_voucher_number || '—'}</TableCell>
                      <TableCell className="whitespace-nowrap font-semibold">{ligne.tractor_plate || '—'}</TableCell>
                      <TableCell className="whitespace-nowrap">{ligne.tank_plate || '—'}</TableCell>
                      <TableCell className="whitespace-nowrap text-right tabular-nums">{formatVolume(ligne.quantity)}</TableCell>
                      <TableCell
                        className={cn(
                          'whitespace-nowrap text-right tabular-nums',
                          (discret || !saisis.prix) && 'text-muted-foreground',
                        )}
                      >
                        {valeur('prix', 'prix', 'Prix', { prix: true })}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'whitespace-nowrap text-right tabular-nums',
                          Number(ligne.montant) < 0 && 'text-destructive',
                          discret && 'text-muted-foreground',
                        )}
                      >
                        {valeur('montant', 'montant', 'Montant')}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {ligne.shortage_known ? (
                          <span className={cn(discret && 'text-muted-foreground')}>{formatVolume(ligne.manquant)}</span>
                        ) : (
                          <div className="flex flex-col items-end gap-0.5">
                            <Input
                              type="number"
                              inputMode="decimal"
                              min="0"
                              step="any"
                              value={saisie.livre[ligne.lot_truck_id] ?? ''}
                              disabled={disabled}
                              onChange={(evenement) => onLivre(ligne.lot_truck_id, evenement.target.value)}
                              placeholder="Dépoté (L)"
                              aria-label={`Dépoté du camion ${ligne.tractor_plate || ''}`.trim()}
                              className="h-8 w-28 text-right tabular-nums"
                            />
                            {!ligne.manquantInconnu ? (
                              <span className="text-xs text-muted-foreground">{formatVolume(ligne.manquant)}</span>
                            ) : null}
                          </div>
                        )}
                      </TableCell>
                      {ligne.already_retained ? (
                        // Un avoir a déjà pris ce manquant : pas de seconde retenue.
                        <TableCell className="text-right">
                          <Badge variant="muted">Déjà retenu</Badge>
                        </TableCell>
                      ) : (
                        <TableCell
                          className={cn('whitespace-nowrap text-right tabular-nums', discret && 'text-muted-foreground')}
                        >
                          <div className="flex flex-col items-end">
                            {valeur('retenue', 'retenue', 'Retenue')}
                            {ligne.litresRetenus && !saisis.retenue ? (
                              <span className="text-xs text-muted-foreground">{formatVolume(ligne.litresRetenus)}</span>
                            ) : null}
                          </div>
                        </TableCell>
                      )}
                      <CelluleMontant valeur={ligne.solde} devise={devise} gras discret={discret} />
                      <TableCell>
                        <Pastilles ligne={ligne} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow className="font-bold" data-testid="decompte-totaux">
                  <TableCell />
                  <TableCell colSpan={4}>Total</TableCell>
                  <TableCell className="whitespace-nowrap text-right tabular-nums">{formatVolume(totaux.quantite)}</TableCell>
                  <TableCell />
                  <CelluleMontant valeur={totaux.montant} devise={devise} />
                  <TableCell />
                  <CelluleMontant valeur={totaux.retenue} devise={devise} />
                  <CelluleMontant valeur={totaux.solde} devise={devise} />
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          </div>
          <BarrePagination pagination={pagination} nom="camions" />
        </>
      )}
    </section>
  );
};

/**
 * Les camions d'un décompte validé ou annulé, comme sur le décompte imprimé :
 * DATE | N° BON | TRACTEUR | CITERNE | QUANTITÉ | PRIX | MONTANT | MANQUANT |
 * RETENUE | SOLDE, et la ligne de totaux. Ce sont les chiffres figés du serveur.
 */
export const LignesDecompte = ({ lignes, totaux, devise = 'XOF' }) => (
  <section className="space-y-2" aria-label="Camions du décompte" data-testid="decompte-lignes-lecture">
    <h2 className="text-sm font-bold text-foreground">Camions</h2>
    <div className="overflow-x-auto rounded-lg border border-border/70">
      <Table stickyFirstColumn={false}>
        <TableHeader>
          <TableRow>
            <TableHead>Chargement</TableHead>
            <TableHead>N° bon</TableHead>
            <TableHead>Tracteur</TableHead>
            <TableHead>Citerne</TableHead>
            <TableHead className="text-right">Quantité</TableHead>
            <TableHead className="text-right">Prix</TableHead>
            <TableHead className="text-right">Montant</TableHead>
            <TableHead className="text-right">Manquant</TableHead>
            <TableHead className="text-right">Retenue</TableHead>
            <TableHead className="text-right">Solde</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lignes.map((ligne) => (
            <TableRow key={ligne.id || ligne.lot_truck_id} data-testid={`decompte-ligne-lecture-${ligne.lot_truck_id}`}>
              <TableCell className="whitespace-nowrap tabular-nums">{formatDate(ligne.loading_date)}</TableCell>
              <TableCell className="whitespace-nowrap tabular-nums">{ligne.loading_voucher_number || '—'}</TableCell>
              <TableCell className="whitespace-nowrap font-semibold">{ligne.tractor_plate || '—'}</TableCell>
              <TableCell className="whitespace-nowrap">{ligne.tank_plate || '—'}</TableCell>
              <TableCell className="whitespace-nowrap text-right tabular-nums">{formatVolume(ligne.quantity)}</TableCell>
              <TableCell
                className={cn(
                  'whitespace-nowrap text-right tabular-nums',
                  !estSaisi(ligne.price_override) && 'text-muted-foreground',
                )}
              >
                <ValeurLue
                  valeur={ligne.price}
                  defaut={ligne.price_default}
                  saisi={estSaisi(ligne.price_override)}
                  devise={devise}
                  prix
                  testid={`decompte-lecture-prix-${ligne.lot_truck_id}`}
                />
              </TableCell>
              <TableCell
                className={cn('whitespace-nowrap text-right tabular-nums', Number(ligne.amount) < 0 && 'text-destructive')}
              >
                <ValeurLue
                  valeur={ligne.amount}
                  defaut={ligne.amount_default}
                  saisi={estSaisi(ligne.amount_override)}
                  devise={devise}
                  testid={`decompte-lecture-montant-${ligne.lot_truck_id}`}
                />
              </TableCell>
              <TableCell className="whitespace-nowrap text-right tabular-nums">
                {ligne.shortage_known ? formatVolume(ligne.shortage_liters) : '—'}
              </TableCell>
              {ligne.already_retained ? (
                <TableCell className="text-right">
                  <Badge variant="muted">Déjà retenu</Badge>
                </TableCell>
              ) : (
                <TableCell className="whitespace-nowrap text-right tabular-nums">
                  <ValeurLue
                    valeur={ligne.retention}
                    defaut={ligne.retention_default}
                    saisi={estSaisi(ligne.retention_override)}
                    devise={devise}
                    testid={`decompte-lecture-retenue-${ligne.lot_truck_id}`}
                  />
                </TableCell>
              )}
              <CelluleMontant valeur={ligne.balance} devise={devise} gras />
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow className="font-bold" data-testid="decompte-totaux-lecture">
            <TableCell colSpan={4}>Total</TableCell>
            <TableCell className="whitespace-nowrap text-right tabular-nums">{formatVolume(totaux.quantite)}</TableCell>
            <TableCell />
            <CelluleMontant valeur={totaux.montant} devise={devise} />
            <TableCell />
            <CelluleMontant valeur={totaux.retenue} devise={devise} />
            <CelluleMontant valeur={totaux.solde} devise={devise} />
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  </section>
);
